import { Col, Row, Table } from "antd";
import type { TableProps } from "antd";
import { useEffect, useState } from "react";
import BarChart from "../components/Charts/BarChart";
import TailSpinLoader from "../components/Items/Loaders/TailSpinLoader";
import NetwalaSelect from "../components/Items/Select/Index";
import useAxiosPost from "../hooks/useAxiosPost";
import useMultiNotification from "../hooks/useNotification";
import moment from "moment";
import ViewReceiptIcon from "../assets/svgs/ViewReceiptIcon.svg";
import ViewReceiptModal from "../components/Modals/ViewReceiptModal";
import "./StaffCollectionPage.css";

interface CollectionStaff {
  intPkHeadID: number;
  staffName: string;
  firstName: string;
  lastName: string;
  totalAmount: number;
  collectionCount: number;
}

interface Collection {
  sNo: string;
  intPkTransactionID: number;
  description: string;
  dateCreated: string;
  intPkResourceFileID: number | null;
  amount: number;
}

const StaffCollectionPage = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const [dateFilter, setDateFilter] = useState<string>("today");

  const [
    getCollectionsByDateRange,
    collectionsByDateRangeResponse,
    collectionsByDateRangeError,
    collectionsByDateRangeLoading,
  ] = useAxiosPost<ResponseProp>("reports/getCollectionsByDateRange");

  const [
    getLast5Collections,
    last5CollectionsResponse,
    last5CollectionsError,
    last5CollectionsLoading,
  ] = useAxiosPost<ResponseProp>("reports/getLast5Collections");

  const [collectionsData, setCollectionsData] = useState<CollectionStaff[]>([]);
  const [last5Collections, setLast5Collections] = useState<Collection[]>([]);
  const [showSlipModal, setShowSlipModal] = useState<boolean>(false);
  const [intPkResourceFileID, setIntPkResourceFileID] = useState<number | null>(
    null
  );

  const dateFilterOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "7 Days", value: "7 days" },
    { label: "30 Days", value: "30 days" },
    { label: "90 Days", value: "90 days" },
  ];

  // Three colors to randomly assign to staff
  const chartColors = ["#FFA500", "#27D538", "#8B7EC8"]; // Orange, Green, Purple

  // Fetch last 5 collections only once on component mount
  useEffect(() => {
    getLast5Collections({});
  }, []);

  // Fetch collections by date range whenever dateFilter changes
  useEffect(() => {
    getCollectionsByDateRange({ dateFilter });
  }, [dateFilter]);

  useEffect(() => {
    if (collectionsByDateRangeResponse !== null) {
      const data: any = collectionsByDateRangeResponse.responseData;
      if (data?.collections) {
        setCollectionsData(data.collections);
      }
    }
  }, [collectionsByDateRangeResponse]);

  useEffect(() => {
    if (last5CollectionsResponse !== null) {
      const data: any = last5CollectionsResponse.responseData;
      if (data?.collections) {
        // Format collections data
        const formatted = data.collections.map(
          (collection: any, index: number) => ({
            sNo: String(index + 1).padStart(2, "0"),
            intPkTransactionID: collection.intPkTransactionID,
            description: collection.description || "",
            dateCreated: collection.dateCreated,
            intPkResourceFileID: collection.intPkResourceFileID || null,
            amount: Number(collection.amount || 0),
          })
        );
        setLast5Collections(formatted);
      }
    }
  }, [last5CollectionsResponse]);

  useEffect(() => {
    if (collectionsByDateRangeError) {
      openNotification("error", "Error", collectionsByDateRangeError);
    }
  }, [collectionsByDateRangeError]);

  useEffect(() => {
    if (last5CollectionsError) {
      openNotification("error", "Error", last5CollectionsError);
    }
  }, [last5CollectionsError]);

  const handleCloseSlipModal = (isReload: boolean) => {
    setIntPkResourceFileID(null);
    setShowSlipModal(false);
  };

  // Prepare chart data - assign colors randomly to staff
  const chartLabels = collectionsData.map((item) => item.staffName);
  const chartSeries = collectionsData.map((item) => item.totalAmount);
  const chartColorsAssigned = collectionsData.map((item, index) => {
    // Randomly assign one of the three colors
    return chartColors[index % chartColors.length];
  });

  // Table columns matching the image format
  const columns: TableProps<Collection>["columns"] = [
    {
      title: "S.No",
      key: "sNo",
      width: 80,
      render: (_: any, __: any, index: number) => (
        <span>{String(index + 1).padStart(2, "0")}</span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string) => text || "-",
    },
    {
      title: "Date & Time",
      dataIndex: "dateCreated",
      key: "dateCreated",
      render: (text: string) => (
        <span className="date-time-box">
          {moment(text).format("DD-MM-YYYY, h:mm a")}
        </span>
      ),
    },
    {
      title: "Invoice pic",
      key: "invoicePic",
      width: 120,
      render: (record: Collection) => (
        <div className="invoice-icon">
          {record.intPkResourceFileID ? (
            <img
              src={ViewReceiptIcon}
              className="cursor-pointer"
              onClick={() => {
                setShowSlipModal(true);
                setIntPkResourceFileID(record.intPkResourceFileID);
              }}
              alt="View Receipt"
            />
          ) : (
            <span>-</span>
          )}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (text: number) => (
        <span>
          {Number(text).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      {showSlipModal && intPkResourceFileID !== null && (
        <ViewReceiptModal
          show={showSlipModal}
          handleClose={handleCloseSlipModal}
          intPkResourceFileID={intPkResourceFileID}
        />
      )}
      <div className="staff-collection-page">
        {collectionsByDateRangeLoading || last5CollectionsLoading ? (
          <TailSpinLoader />
        ) : (
          <>
            <Row gutter={[24, 24]} style={{ marginBottom: "16px" }}>
              <Col span={24}>
                <div className="filters-container">
                  <div className="filter-item">
                    <NetwalaSelect
                      value={dateFilter}
                      onChange={(value) => setDateFilter(value)}
                      options={dateFilterOptions}
                      size="large"
                      height="45px"
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
              <Col span={24}>
                <div className="chart-container">
                  {collectionsData.length > 0 ? (
                    <BarChart
                      labels={chartLabels}
                      series={chartSeries}
                      colors={chartColorsAssigned}
                    />
                  ) : (
                    <div className="no-data">No collections data available</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <div className="table-container">
                  <h3 className="table-title">Last 5 Collections</h3>
                  <Table
                    dataSource={last5Collections}
                    columns={columns}
                    pagination={false}
                    rowKey="intPkTransactionID"
                    className="netwala-users-table striped-table"
                    scroll={{ x: "max-content" }}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
};

export default StaffCollectionPage;
